/**
 * The pages of the board this screen is on (SRS C1).
 *
 * A board is one or more pages and only one of them is on the canvas at a time, so
 * turning a page means taking the last one off this screen and putting the next one
 * down — never deleting anything. Everything each page holds stays in the shared
 * document exactly as it was left.
 *
 * The paper lives here too, because SRS C7 makes it a property of the page: page one
 * may be squared while page two is blank.
 */

import type { PageBackground } from '@opal/board-doc';

import type { InkSession } from './ink-session.js';
import { createPageBook, pageAfterRemoval, type PageBook } from './pages.js';
import type { Presence } from './presence.js';
import type { SelectSession } from './select-session.js';
import { useBoardStore } from './store.js';
import type { TextSession } from './text-session.js';
import { LOCAL_ORIGIN, type BoardSync } from './sync.js';

/** SRS C1 — everything the tab strip along the bottom of the board can do. */
export interface PageActions {
  /** Turns to a page. Everything on the one being left stays where it is. */
  goTo(pageId: string): void;
  /** Adds an empty page at the end and turns to it. */
  add(): void;
  rename(pageId: string, name: string): void;
  /** Copies a page, work and all, puts it next to the original and turns to it. */
  duplicate(pageId: string): void;
  /** Takes a page away. Does nothing when it is the only page the board has. */
  remove(pageId: string): void;
  /** Drags a page to another place in the strip. */
  move(pageId: string, toIndex: number): void;
}

/** The part of a board screen the pages need to reach. */
export interface PageHost {
  sync: BoardSync | null;
  presence: Presence | null;
  book: PageBook | null;
  /** The paper as this file last saw it, so its own write is not read back again. */
  paper: PageBackground | null;
  board: {
    readonly ink: InkSession;
    readonly select: SelectSession;
    readonly text: TextSession;
    readonly laser: { cancel(): void };
  } | null;
  /** SRS D6 — set on the device everybody else is following. */
  presenting: boolean;
  /** SRS D6 — set while this device is keeping step with the presenter. */
  following: boolean;
  stopListening: (() => void)[];
}

/** True when two papers are the same paper, so a write is not echoed back and forth. */
function samePaper(a: PageBackground | null, b: PageBackground | null): boolean {
  if (!a || !b) return a === b;
  return a.type === b.type && a.gridSize === b.gridSize && a.color === b.color;
}

/** SRS C7 — puts the page's own paper under the board. */
function readPaper(state: PageHost, pageId: string): void {
  const paper = state.book?.background(pageId) ?? null;
  if (!paper || samePaper(paper, state.paper)) return;
  state.paper = paper;
  useBoardStore.getState().setBackground(paper);
}

/** SRS C7 — and writes a change to it back, so it is the page's and not the tab's. */
export function writePaper(state: PageHost, paper: PageBackground): void {
  const pageId = state.sync?.pageId();
  if (!pageId || samePaper(paper, state.paper)) return;
  state.paper = paper;
  state.book?.setBackground(pageId, paper);
}

/**
 * The page changed (SRS C1).
 *
 * The last page is taken off this screen before the new one arrives, so the two are
 * never on the canvas together. Nothing is deleted by this: every page's work stays
 * in the shared document exactly as it was left.
 */
export function turnedPage(state: PageHost, pageId: string): void {
  const store = useBoardStore.getState();
  const before = store.activePageId;
  // Only when turning AWAY from a page. The board arriving is not a page change, and
  // clearing then would wipe the lines drawn while it was still on its way.
  if (before !== null && before !== pageId) {
    state.board?.ink.clear();
    state.board?.select.clear();
    state.board?.text.cancel();
    state.board?.laser.cancel();
  }
  state.presence?.setPage(pageId);
  store.setActivePage(pageId);
  readPaper(state, pageId);
}

/** The page the presenting teacher is on, or null when nobody is presenting. */
function presenterPageOf(state: PageHost): string | null {
  for (const other of state.presence?.others() ?? []) {
    if (other.role === 'student' || !other.view) continue;
    return other.pageId === '' ? null : other.pageId;
  }
  return null;
}

/** SRS D6 — a class following the teacher turns the pages the teacher turns. */
export function followPresenterPage(state: PageHost): void {
  if (state.presenting || !state.following) return;
  const pageId = presenterPageOf(state);
  if (pageId && pageId !== state.sync?.pageId()) state.sync?.setPage(pageId);
}

/** The page to land on once one has been taken away (SRS C1). */
function afterRemoval(state: PageHost, pageId: string): string | null {
  const before = state.book?.list() ?? [];
  const index = before.findIndex((page) => page.id === pageId);
  if (index < 0) return null;
  return pageAfterRemoval(before.filter((page) => page.id !== pageId), index);
}

/** SRS C1 — what the tab strip along the bottom does. */
export function pageActions(state: PageHost): PageActions {
  const goTo = (pageId: string): void => {
    state.sync?.setPage(pageId);
  };
  return {
    goTo,
    add() {
      const pageId = state.book?.add();
      if (pageId) goTo(pageId);
    },
    rename(pageId, name) {
      state.book?.rename(pageId, name);
    },
    duplicate(pageId) {
      const copy = state.book?.duplicate(pageId);
      if (copy) goTo(copy);
    },
    remove(pageId) {
      const landing = afterRemoval(state, pageId);
      if (!state.book?.remove(pageId)) return;
      // Turn away from it first only when it is the page being worked on; a page
      // deleted from somewhere else in the strip leaves the view where it is.
      if (landing && state.sync?.pageId() === pageId) goTo(landing);
    },
    move(pageId, toIndex) {
      state.book?.move(pageId, toIndex);
    },
  };
}

/** Watches the pages: the strip, the page being worked on, and the paper under it. */
export function watchPages(state: PageHost, sync: BoardSync): void {
  const book = createPageBook(sync.doc, LOCAL_ORIGIN);
  state.book = book;
  state.stopListening.push(
    book.onChanged((pages) => {
      useBoardStore.getState().setPages(pages);
      const pageId = sync.pageId();
      // A page deleted on somebody else's screen takes this one with it.
      if (pageId && !pages.some((page) => page.id === pageId)) {
        const first = pages[0];
        if (first) sync.setPage(first.id);
        return;
      }
      if (pageId) readPaper(state, pageId);
    }),
    sync.onPageChanged((pageId) => {
      turnedPage(state, pageId);
    }),
    () => {
      book.dispose();
    },
  );
}

